/**
 * Saying which change the server refused.
 *
 * The banner used to count refusals without naming any, so a tech with three
 * jobs' worth of work had to guess which part or hour had not landed.
 */

import * as outbox from "./outbox";
import { get, type Row } from "./store";

/** How many refusals the banner lists before it stops. */
export const MAX_REFUSED_LINES = 3;

function humanise(entity: string): string {
  const text = entity.replace(/_/g, " ");
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** `Part "Turret" on J-1234: <reason>`; the job number is left out when unknown. */
export function describeRefused(change: outbox.Queued, jobNumber?: string | null): string {
  const value = change.value && typeof change.value === "object" ? (change.value as Record<string, unknown>) : {};

  let what: string;
  if (change.entity === "job_line") {
    const description = typeof value.description === "string" ? value.description.trim() : "";
    what = description ? `Part "${description}"` : "A part";
  } else if (change.entity === "time_entry") {
    what = "Time";
  } else {
    what = humanise(change.entity);
  }

  const where = jobNumber ? ` on ${jobNumber}` : "";
  const why = change.reason?.trim() || "refused by the server";
  return `${what}${where}: ${why}`;
}

/** The first `limit` refused changes, as banner lines. */
export async function refusedLines(limit = MAX_REFUSED_LINES): Promise<string[]> {
  const refused = await outbox.rejected();
  const lines: string[] = [];
  for (const change of refused.slice(0, limit)) {
    const value = change.value && typeof change.value === "object" ? (change.value as Record<string, unknown>) : {};
    let number: string | null = null;
    const jobId = change.entity === "job" ? change.entity_id : value.job_id;
    if (typeof jobId === "string") {
      try {
        const job = await get<Row>("job", jobId);
        number = job && typeof job.number === "string" && job.number ? job.number : null;
      } catch {
        number = null;
      }
    }
    lines.push(describeRefused(change, number));
  }
  return lines;
}
